import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Input, Loader } from 'components/UIElements'
import DcfSignaturePad from './DcfSignaturePad'
import { SIGNER_UI_STATES } from '../../../utils/pdfSignerTypes'

/**
 * Password reauth + optional draw mark for a single e-Sign placement.
 * Name/title/email are prefilled from the signed-in account when available.
 */
const PdfSignerModal = ({
  open,
  roleLabel,
  requiresDecision = false,
  decision = 'approved',
  onDecisionChange,
  onCancel,
  onConfirm,
  busy = false,
  errorText = '',
  initialSignerName = '',
  initialSignerTitle = '',
  initialSignerEmail = '',
}) => {
  const [password, setPassword] = useState('')
  const [signerName, setSignerName] = useState('')
  const [signerTitle, setSignerTitle] = useState('')
  const [signatureData, setSignatureData] = useState('')
  const [localError, setLocalError] = useState('')

  // This modal stays mounted while closed, so the password and drawn mark are
  // dropped on both edges. Clearing only on open still renders one frame with
  // the previous signature, which the pad then paints.
  useEffect(() => {
    setPassword('')
    setSignatureData('')
    setLocalError('')
  }, [open])

  useEffect(() => {
    if (!open) return
    setSignerName(initialSignerName || '')
    setSignerTitle(initialSignerTitle || '')
  }, [open, initialSignerName, initialSignerTitle])

  if (!open) return null

  const onSubmit = () => {
    setLocalError('')
    if (!password) {
      setLocalError('Re-enter your password to continue.')
      return
    }
    if (!signerName.trim()) {
      setLocalError('Enter the signee name to continue.')
      return
    }
    onConfirm({
      password,
      signerName: signerName.trim(),
      signerTitle: signerTitle.trim(),
      signerEmail: (initialSignerEmail || '').trim() || undefined,
      signatureData,
      decision,
    })
  }

  return (
    <div className='dcf-esign-modal-backdrop' role='presentation'>
      <div className='dcf-esign-modal' role='dialog' aria-modal='true' aria-label={`Sign as ${roleLabel}`}>
        <header>
          <h4>Sign as {roleLabel}</h4>
          <p>
            Re-enter your password and confirm the signee name. Drawing a mark is optional — the
            typed name is stamped when no mark is drawn.
          </p>
        </header>
        {(errorText || localError) && (
          <div className='dcf-esign-error'>
            <i className='fas fa-exclamation-circle' /> {errorText || localError}
          </div>
        )}
        <label htmlFor='dcf-esign-password'>Password</label>
        <Input
          id='dcf-esign-password'
          type='password'
          value={password}
          disabled={busy}
          onChange={setPassword}
          placeholder='Current account password'
          autoComplete='current-password'
        />
        {requiresDecision && (
          <div className='dcf-esign-decision'>
            <label htmlFor='dcf-esign-decision'>Decision</label>
            <select
              id='dcf-esign-decision'
              value={decision}
              disabled={busy}
              onChange={event => onDecisionChange && onDecisionChange(event.target.value)}
            >
              <option value='approved'>Approved</option>
              <option value='rejected'>Rejected</option>
            </select>
          </div>
        )}
        <label htmlFor='dcf-esign-name'>Signee name</label>
        <Input
          id='dcf-esign-name'
          value={signerName}
          disabled={busy}
          onChange={setSignerName}
          placeholder='Full name of person signing'
        />
        {initialSignerEmail ? (
          <>
            <label htmlFor='dcf-esign-email'>Email</label>
            <Input id='dcf-esign-email' value={initialSignerEmail} disabled />
          </>
        ) : null}
        <label htmlFor='dcf-esign-title'>Title (optional)</label>
        <Input
          id='dcf-esign-title'
          value={signerTitle}
          disabled={busy}
          onChange={setSignerTitle}
          placeholder='Title'
        />
        <div className='dcf-esign-pad'>
          <label htmlFor='dcf-esign-pad'>Signature mark (optional)</label>
          <DcfSignaturePad signature={signatureData} disabled={busy} onChange={setSignatureData} />
        </div>
        <footer>
          <Button secondary content='Cancel' disabled={busy} onClick={onCancel} />
          <Button
            id='dcf-esign-confirm'
            content={busy ? 'Signing…' : 'Verify & stamp'}
            disabled={busy}
            onClick={onSubmit}
          />
        </footer>
        {busy && (
          <div className='dcf-esign-busy'>
            <Loader inBlock />
            <span>{SIGNER_UI_STATES.REAUTHENTICATING}</span>
          </div>
        )}
      </div>
    </div>
  )
}

PdfSignerModal.propTypes = {
  open: PropTypes.bool,
  roleLabel: PropTypes.string,
  requiresDecision: PropTypes.bool,
  decision: PropTypes.string,
  onDecisionChange: PropTypes.func,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  busy: PropTypes.bool,
  errorText: PropTypes.string,
  initialSignerName: PropTypes.string,
  initialSignerTitle: PropTypes.string,
  initialSignerEmail: PropTypes.string,
}

export default PdfSignerModal
